// Puzzle Studio client and TanStack Query hooks (contracts/studio.md §3–4). Reads are open to every
// role; mutations need editor (the kill switch, a config key, needs admin). Kept apart from client.ts /
// queries.ts so the Studio is one module.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, request } from "./client";
import { keys } from "./queries";
import { PuzzleList, type AnswerValue, type PuzzleSource, type PuzzleStatus, type ReviewState } from "./schemas";
import {
  BankList,
  BankScheduleResult,
  BankVerifyResult,
  StudioJobDetail,
  StudioJobResult,
  StudioJobs,
  StudioTypes,
  StudioUsage,
  type DifficultyRange,
  type JobStatus,
  type ScheduleTarget,
  type StudioJob,
} from "./studioSchemas";

const enc = encodeURIComponent;

export interface BankQuery {
  source?: PuzzleSource | "";
  category?: string;
  type?: string;
  review?: ReviewState | "";
  status?: PuzzleStatus | "";
  /** Case-insensitive substring of the id (curated ids embed the sourceId: `bank-pattern-24`). */
  q?: string;
  cursor?: string | null;
  limit?: number;
}

export interface NewJob {
  kind: "procedural" | "ai";
  type: string;
  count: number;
  difficulty: DifficultyRange;
  model?: string;
  theme?: string;
}

export interface SpecEdit {
  spec: unknown;
  answer: AnswerValue;
  explanation: string;
}

export const studioApi = {
  /** The engine's catalogue (503 engine_unavailable when it is down) and whether Claude can run. */
  types: (signal?: AbortSignal) => request(StudioTypes, "GET", "/studio/types", { signal }),
  jobs: (status: JobStatus | null, cursor?: string | null, limit = 20, signal?: AbortSignal) =>
    request(StudioJobs, "GET", "/studio/jobs", { query: { status, cursor, limit }, signal }),
  job: (id: string, signal?: AbortSignal) => request(StudioJobDetail, "GET", `/studio/jobs/${enc(id)}`, { signal }),
  /** 201 { job }. */
  createJob: (body: NewJob) => request(StudioJobResult, "POST", "/studio/jobs", { body }),
  /** Queued → cancelled at once; running → cancelRequested (stops after its batch). */
  cancelJob: (id: string) => request(StudioJobResult, "POST", `/studio/jobs/${enc(id)}/cancel`),
  /** `month` is YYYY-MM (UTC); the server defaults to the current month. */
  usage: (month?: string | null, signal?: AbortSignal) => request(StudioUsage, "GET", "/studio/usage", { query: { month }, signal }),

  bank: (q: BankQuery, signal?: AbortSignal) =>
    request(BankList, "GET", "/puzzles", { query: { ...q, mode: "bank", limit: q.limit ?? 50 }, signal }),
  /** What a day holds now (the schedule's `mode=daily|hard` list, one day). */
  day: (target: ScheduleTarget, date: string, signal?: AbortSignal) =>
    request(PuzzleList, "GET", "/puzzles", { query: { mode: target, from: date, to: date, limit: 5 }, signal }),
  verify: (id: string) => request(BankVerifyResult, "POST", `/puzzles/${enc(id)}/verify`),
  editSpec: (id: string, body: SpecEdit) => request(BankVerifyResult, "PATCH", `/puzzles/${enc(id)}/spec`, { body }),
  schedule: (id: string, target: ScheduleTarget, date: string) =>
    request(BankScheduleResult, "POST", `/puzzles/${enc(id)}/schedule`, { body: { target, date } }),
};

export const studioKeys = {
  all: ["studio"] as const,
  types: ["studio", "types"] as const,
  jobs: (status: JobStatus | null, cursor: string | null) => ["studio", "jobs", status, cursor] as const,
  job: (id: string) => ["studio", "job", id] as const,
  usage: (month: string | null) => ["studio", "usage", month] as const,
  bank: (q: BankQuery) => ["studio", "bank", q] as const,
  day: (target: ScheduleTarget, date: string) => ["studio", "day", target, date] as const,
  reviewQueue: ["studio", "review-queue"] as const,
};

const isActive = (j: Pick<StudioJob, "status">) => j.status === "queued" || j.status === "running";
/** Jobs refresh every ~3 s while any is queued or running, and stop when idle. */
export const LIVE_INTERVAL_MS = 3000;

export const useStudioTypes = () =>
  useQuery({ queryKey: studioKeys.types, queryFn: ({ signal }) => studioApi.types(signal), staleTime: 60_000 });

export const useStudioUsage = (month: string | null = null) =>
  useQuery({ queryKey: studioKeys.usage(month), queryFn: ({ signal }) => studioApi.usage(month, signal), placeholderData: keepPreviousData });

export const useStudioJobs = (status: JobStatus | null, cursor: string | null = null, limit = 20) =>
  useQuery({
    queryKey: [...studioKeys.jobs(status, cursor), limit],
    queryFn: ({ signal }) => studioApi.jobs(status, cursor, limit, signal),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.items.some(isActive) ? LIVE_INTERVAL_MS : false),
  });

export const useStudioJob = (id: string | undefined) =>
  useQuery({
    queryKey: studioKeys.job(id ?? ""),
    queryFn: ({ signal }) => studioApi.job(id!, signal),
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data && isActive(query.state.data.job) ? LIVE_INTERVAL_MS : false),
  });

export const useBank = (q: BankQuery) =>
  useQuery({ queryKey: studioKeys.bank(q), queryFn: ({ signal }) => studioApi.bank(q, signal), placeholderData: keepPreviousData });

/** Puzzles waiting for a person (types without a solver), up to 200. */
export const useReviewQueue = () =>
  useQuery({ queryKey: studioKeys.reviewQueue, queryFn: ({ signal }) => studioApi.bank({ review: "pending", limit: 200 }, signal) });

export const useDay = (target: ScheduleTarget, date: string | null) =>
  useQuery({
    queryKey: studioKeys.day(target, date ?? ""),
    queryFn: ({ signal }) => studioApi.day(target, date!, signal),
    enabled: Boolean(date),
    staleTime: 0,
  });

// ---------------------------------------------------------------- mutations

/** Everything a Studio or bank mutation can change: Studio views, the puzzle bank and schedule, the audit log. */
function useInvalidateStudio() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: studioKeys.all }),
      qc.invalidateQueries({ queryKey: ["puzzles"] }),
      qc.invalidateQueries({ queryKey: ["puzzle"] }),
      qc.invalidateQueries({ queryKey: ["schedule"] }),
      qc.invalidateQueries({ queryKey: ["dashboard"] }),
      qc.invalidateQueries({ queryKey: keys.audit }),
    ]);
}

export function useCreateJob() {
  const invalidate = useInvalidateStudio();
  return useMutation({ mutationFn: (body: NewJob) => studioApi.createJob(body), onSuccess: invalidate });
}

export function useCancelJob() {
  const invalidate = useInvalidateStudio();
  return useMutation({ mutationFn: (id: string) => studioApi.cancelJob(id), onSuccess: invalidate });
}

/** The review is the existing PATCH /puzzles/:id {status}: approve → reviewState approved, reject → rejected. */
export function useReview() {
  const invalidate = useInvalidateStudio();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PuzzleStatus }) => api.setPuzzleStatus(id, status),
    onSuccess: invalidate,
  });
}

export function useVerify() {
  const invalidate = useInvalidateStudio();
  return useMutation({ mutationFn: (id: string) => studioApi.verify(id), onSuccess: invalidate });
}

export function useEditSpec() {
  const invalidate = useInvalidateStudio();
  return useMutation({ mutationFn: ({ id, edit }: { id: string; edit: SpecEdit }) => studioApi.editSpec(id, edit), onSuccess: invalidate });
}

export function useSchedulePuzzle() {
  const invalidate = useInvalidateStudio();
  return useMutation({
    mutationFn: ({ id, target, date }: { id: string; target: ScheduleTarget; date: string }) => studioApi.schedule(id, target, date),
    onSuccess: invalidate,
  });
}

/** The kill switch is the config key studio.aiEnabled (PUT /config/:key, admin only). */
export function useSetAiEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.setConfig("studio.aiEnabled", enabled),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: studioKeys.all }),
        qc.invalidateQueries({ queryKey: keys.config }),
        qc.invalidateQueries({ queryKey: keys.audit }),
      ]),
  });
}
